import React, { ReactNode } from 'react';
import { Navigate, Route, Routes, Outlet } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useQuery } from '../api/useQuery';
import Steder from './steder.uha';
import translation from './activity.uha.i18n';
import RolleListe from './rolleliste.uha';
import Registrering from './registrering';
import Fordeling from './fordeling/fordeling.uha';
import Detaljer from './detaljer.uha';
import Oppsummering from './oppsummering.uha';
import * as css from './activityTabs.module.scss';
import { useActivityContextUHA } from './activitycontexts';
import { dateTimeNorwegianLocale } from '../date.utils';
import VurderingWrapper from './vurdering/vurderingWrapper';
import { devEnvFlag } from '../util/featureFlags';
import { Tab, Tabs } from '../components/tabs/tabs';
import { PageNotFoundWithoutLayout } from '../pages/pagenotfound';
import { useFormatMessageFor } from '../i18n/translation';

export enum ActivityTab {
  DETALJER = 'detaljer',
  STEDER = 'steder',
  ROLLER = 'roller',
  REGISTRERING = 'registrering',
  VURDERING = 'vurdering',
  FORDELING = 'fordeling',
  OPPSUMMERING = 'oppsummering',
}

interface ActivityTabsProps {
  isCreating: boolean;
}

const ActivityTabs = ({ isCreating }: ActivityTabsProps) => {
  const {
    praksisaktivitet: { registreringsVarsler },
    reloadPraksisaktivitet,
  } = useActivityContextUHA();

  const labels = useFormatMessageFor(translation);

  return (
    <Tabs>
      {Object.values(ActivityTab)
        .filter((value) => devEnvFlag || value !== ActivityTab.VURDERING)
        .map((tabName) => {
          const isDisabled = isCreating && tabName === ActivityTab.DETALJER;

          return (
            <Tab
              to={tabName}
              key={tabName}
              id={tabName}
              disabled={isDisabled}
              onClick={reloadPraksisaktivitet}
              onKeyUp={(e) => {
                if (e.key === 'Enter') reloadPraksisaktivitet();
              }}
            >
              {labels[tabName]}
              {registreringsVarsler > 0 &&
                tabName === ActivityTab.REGISTRERING && (
                  <NotificationBadge>{registreringsVarsler}</NotificationBadge>
                )}
            </Tab>
          );
        })}
    </Tabs>
  );
};

interface NotificationBadgeProps {
  children: ReactNode;
}

const NotificationBadge = ({ children }: NotificationBadgeProps) => (
  <div className={css.notificationBadge}>{children}</div>
);

const ActivityTabsRoutes = () => {
  const isCreating = !!useQuery().get('create');

  return (
    <Routes>
      <Route
        path="/"
        element={
          <>
            <ActivityTabs isCreating={isCreating} />
            <Outlet />
            <EndretAv />
          </>
        }
      >
        <Route
          path=""
          element={
            <Navigate
              to={isCreating ? ActivityTab.DETALJER : ActivityTab.STEDER}
              replace
            />
          }
        />
        {devEnvFlag && (
          <Route path={ActivityTab.VURDERING} element={<VurderingWrapper />} />
        )}
        <Route path={ActivityTab.STEDER} element={<Steder />} />
        <Route path={ActivityTab.ROLLER} element={<Roles />} />
        <Route path={ActivityTab.REGISTRERING} element={<Registrering />} />
        <Route path={ActivityTab.FORDELING} element={<Fordeling />} />
        <Route path={ActivityTab.OPPSUMMERING} element={<Oppsummering />} />
        <Route path={ActivityTab.DETALJER} element={<Detaljer />} />
        <Route path="*" element={<PageNotFoundWithoutLayout />} />
      </Route>
    </Routes>
  );
};

const EndretAv = React.memo(() => {
  const { formatMessage } = useIntl();
  const { praksisaktivitet } = useActivityContextUHA();
  const { endret } = praksisaktivitet;

  if (!endret || !endret.endretAv || !endret.endretTidspunkt) return <></>;

  const { endretAv, endretTidspunkt } = endret;
  const { fornavn, etternavn } = endretAv;

  const label = formatMessage(
    { id: translation.endretAv.id },
    {
      fornavn,
      etternavn,
      endretTidspunkt: dateTimeNorwegianLocale(new Date(endretTidspunkt)),
    },
  );

  return <div className={css.endret}>{label}</div>;
});

const Roles = () => {
  const { praksisaktivitet } = useActivityContextUHA();
  const { emne, sted } = praksisaktivitet;

  return (
    <RolleListe sted={sted.navn[0]} emnekode={emne.kode} emnenavn={emne.navn} />
  );
};

export default ActivityTabsRoutes;
